import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import ConsumerCard from '../compo/consumer_card';
import { fetchConsultations, deleteConsultation, fetchProperties } from '../utils/api';
import { PropertySidebar } from '../compo/card';
import InterestedPropertyModal from '../compo/interested_property_modal';
import ButtonListFunction from '../compo/button_list_function';
import RollbackModal from '../compo/rollback_modal';
import { useAuth } from '../contexts/AuthContext';

const MIN_CONSULTATION_HEIGHT = 360;

// 상담 카드 컴포넌트 (접기/펼치기 기능 포함)
function ConsultationCard({ consultation, onEdit, onDelete, filtersToTags, onInterestedPropertyClick }) {
  // 날짜를 정렬 (가장 최근 날짜가 첫 번째)
  const sortedConsultations = useMemo(() => {
    if (!consultation.consultations || consultation.consultations.length === 0) {
      return [];
    }
    return [...consultation.consultations].sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });
  }, [consultation.consultations]);

  // 초기 상태: 가장 최근 날짜만 펼쳐짐
  const getInitialExpandedDates = useCallback(() => {
    if (sortedConsultations.length > 0) {
      const firstKey = `${sortedConsultations[0].date}-0`;
      return new Set([firstKey]);
    }
    return new Set();
  }, [sortedConsultations]);

  // 날짜별 접기/펼치기 상태 관리 (고유 키 사용)
  const [expandedDates, setExpandedDates] = useState(getInitialExpandedDates);
  
  // sortedConsultations가 변경될 때만 초기화 (컴포넌트가 처음 마운트되거나 데이터가 변경될 때)
  const prevSortedConsultationsRef = useRef(sortedConsultations);
  useEffect(() => {
    // sortedConsultations가 변경되었을 때만 초기화
    if (prevSortedConsultationsRef.current !== sortedConsultations) {
      if (sortedConsultations.length > 0) {
        // 가장 최근 날짜만 펼쳐짐 (고유 키 사용)
        const firstKey = `${sortedConsultations[0].date}-0`;
        setExpandedDates(new Set([firstKey]));
      } else {
        setExpandedDates(new Set());
      }
      prevSortedConsultationsRef.current = sortedConsultations;
    }
  }, [sortedConsultations]);

  // 날짜 접기/펼치기 토글
  const toggleDate = useCallback((date) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  }, []);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="space-y-3">
        {/* 헤더 */}
        <div className="flex items-center justify-between pb-2 border-b border-gray-200">
          <h3 
            onClick={onEdit}
            className="text-lg font-semibold text-gray-800 cursor-pointer hover:text-orange-600 transition-colors"
          >
            {consultation.customerName && !consultation.customerName.endsWith('님') 
              ? `${consultation.customerName}님` 
              : consultation.customerName}
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {consultation.createdAt ? new Date(consultation.createdAt).toLocaleDateString('ko-KR') : ''}
            </span>
            <button
              onClick={onDelete}
              className="text-red-500 hover:text-red-700 text-sm px-2 py-1 rounded hover:bg-red-50 transition-colors"
              title="삭제"
            >
              🗑️
            </button>
          </div>
        </div>
        
        {/* 상담 기록 목록 */}
        {sortedConsultations.length > 0 && (
          <div className="space-y-2">
            {sortedConsultations.map((consult, idx) => {
              // 고유 키 생성 (날짜 + 인덱스 조합)
              const uniqueKey = `${consult.date}-${idx}`;
              const isExpanded = expandedDates.has(uniqueKey);
              
              return (
                <div key={uniqueKey} className="bg-gray-50 rounded-lg border border-gray-200">
                  {/* 날짜 헤더 (클릭 가능) */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // 이벤트 전파 방지
                      toggleDate(uniqueKey);
                    }}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-100 transition-colors rounded-lg"
                  >
                    <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <span>{isExpanded ? '▼' : '▶'}</span>
                      <span>📅 {consult.date}</span>
                    </div>
                  </button>
                  
                  {/* 상담 내용 (접기/펼치기) */}
                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-3">
                      {/* 조건 태그 */}
                      {(() => {
                        const conditionTags = filtersToTags(consult.conditions);
                        return conditionTags.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-1">조건</div>
                            <div className="flex flex-wrap gap-1.5">
                              {conditionTags.map((tag, tagIdx) => (
                                <span
                                  key={tagIdx}
                                  className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded-md"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* 관심 매물 태그 */}
                      {(() => {
                        const properties = Array.isArray(consult.interestedProperties) 
                          ? consult.interestedProperties 
                          : (consult.interestedProperties ? consult.interestedProperties.split(',').filter(Boolean) : []);
                        return properties.length > 0 && (
                          <div>
                            <div className="text-xs font-medium text-gray-500 mb-1">관심 매물</div>
                            <div className="flex flex-wrap gap-1.5">
                              {properties.map((propertyName, propIdx) => (
                                <button
                                  key={propIdx}
                                  type="button"
                                  onClick={() => onInterestedPropertyClick(propertyName)}
                                  className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-md hover:bg-blue-200 transition-colors cursor-pointer"
                                >
                                  {propertyName}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* 메모 */}
                      {consult.memo && (
                        <div>
                          <div className="text-xs font-medium text-gray-500 mb-1">메모</div>
                          <div className="text-sm text-gray-600 whitespace-pre-wrap">
                            {consult.memo}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function MainConsultation({ availableHeight, onAddInterestedProperty, onFocusMap }) {
  const { role, loading: authLoading, isAuthenticated } = useAuth();
  const [consultationData, setConsultationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddConsultationOpen, setIsAddConsultationOpen] = useState(false);
  const [editingConsultation, setEditingConsultation] = useState(null);
  const [allProperties, setAllProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isPropertySidebarOpen, setIsPropertySidebarOpen] = useState(false);
  const [isInterestedPropertyModalOpen, setIsInterestedPropertyModalOpen] = useState(false);
  const [propertyForInterested, setPropertyForInterested] = useState(null);
  const [isRollbackModalOpen, setIsRollbackModalOpen] = useState(false);

  // 관심 매물 등록 핸들러 (App.js에서 전달된 prop이 있으면 사용, 없으면 로컬 모달 사용)
  const handleAddInterestedPropertyLocal = useCallback((property) => {
    if (onAddInterestedProperty) {
      // App.js의 모달 사용
      onAddInterestedProperty(property);
    } else {
      // 로컬 모달 사용
      setPropertyForInterested(property);
      setIsInterestedPropertyModalOpen(true);
    }
  }, [onAddInterestedProperty]);

  // 상담 데이터 로드
  const loadConsultations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchConsultations(role); // 역할 정보 전달
      setConsultationData(data);
    } catch (err) {
      console.error('상담 데이터 로드 실패:', err);
      setError(err.message || '상담 데이터를 불러오는데 실패했습니다.');
      setConsultationData([]);
    } finally {
      setIsLoading(false);
    }
  }, [role]);

  // 상담 데이터 추가 (저장 성공 시 호출)
  const handleConsultationAdded = useCallback(() => {
    // 백엔드에서 최신 데이터 다시 불러오기
    loadConsultations();
  }, [loadConsultations]);

  // 상담 데이터 업데이트 (편집 성공 시 호출)
  const handleConsultationUpdated = useCallback(() => {
    setEditingConsultation(null);
    // 백엔드에서 최신 데이터 다시 불러오기
    loadConsultations();
  }, [loadConsultations]);

  // 상담 카드 클릭 핸들러
  const handleConsultationClick = useCallback((consultation) => {
    setEditingConsultation(consultation);
  }, []);

  // 상담 삭제 핸들러
  const handleDeleteConsultation = useCallback(async (e, consultationId) => {
    e.stopPropagation(); // 카드 클릭 이벤트 방지
    
    if (!window.confirm('정말 이 상담 일지를 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteConsultation(consultationId);
      // 백엔드에서 최신 데이터 다시 불러오기
      loadConsultations();
    } catch (err) {
      console.error('상담 삭제 실패:', err);
      alert('상담 삭제에 실패했습니다.');
    }
  }, [loadConsultations]);

  // 상담 데이터 로드 (로그인한 경우에만)
  useEffect(() => {
    // 인증 상태 확인 중이면 대기
    if (authLoading) {
      return;
    }
    
    // 로그인하지 않았으면 상담 로드하지 않음
    if (!isAuthenticated) {
      setIsLoading(false);
      setConsultationData([]);
      return;
    }
    
    loadConsultations();
  }, [authLoading, isAuthenticated, loadConsultations]);

  // App.js의 관심 매물 등록 모달이 성공했을 때 상담 데이터 새로고침
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleConsultationRefresh = () => {
      loadConsultations();
    };
    window.addEventListener('consultationRefresh', handleConsultationRefresh);
    return () => {
      window.removeEventListener('consultationRefresh', handleConsultationRefresh);
    };
  }, [isAuthenticated, loadConsultations]);

  // 매물 데이터 로드 (로그인한 경우에만)
  const loadProperties = useCallback(async () => {
    if (!isAuthenticated) {
      setAllProperties([]);
      return;
    }
    
    try {
      const data = await fetchProperties(role); // 역할 정보 전달
      setAllProperties(data);
    } catch (error) {
      console.error('매물 데이터 로드 실패:', error);
    }
  }, [isAuthenticated, role]);

  useEffect(() => {
    // 인증 상태 확인 중이면 대기
    if (authLoading) {
      return;
    }
    
    loadProperties();
  }, [authLoading, loadProperties]);

  // 필터를 태그로 변환하는 함수
  const filtersToTags = useCallback((filters) => {
    if (!filters || typeof filters !== 'object') return [];
    const tags = [];
    
    const RANGE_FIELDS = [
      { key: '층수', label: '층수' },
      { key: '총층수', label: '총층수' },
      { key: '평수', label: '평수' },
      { key: '보증금', label: '보증금' },
      { key: '월세', label: '월세' },
      { key: '관리비', label: '관리비' },
      { key: '권리비', label: '권리비' },
    ];
    
    // 다중 선택
    if (Array.isArray(filters.상권) && filters.상권.length > 0) {
      tags.push(...filters.상권.map(v => `상권: ${v}`));
    }
    if (Array.isArray(filters.용도) && filters.용도.length > 0) {
      tags.push(...filters.용도.map(v => `용도: ${v}`));
    }
    
    // 단일 선택
    if (filters.엘베) tags.push(`엘베: ${filters.엘베}`);
    if (filters.화장실) tags.push(`화장실: ${filters.화장실}`);
    
    // 관심 매물
    if (filters.관심매물손님) {
      tags.push(`관심 매물: ${filters.관심매물손님}`);
    }
    
    // 범위
    RANGE_FIELDS.forEach(field => {
      const range = filters[field.key];
      if (range && (range.min || range.max)) {
        const min = range.min ? range.min.toString().trim() : '';
        const max = range.max ? range.max.toString().trim() : '';
        
        if (min && max) {
          // 둘 다 있으면 범위로 표시
          tags.push(`${field.label}: ${min}~${max}${getUnit(field.key)}`);
        } else if (min) {
          // 최소값만 있으면
          tags.push(`${field.label}: ${min}${getUnit(field.key)} 이상`);
        } else if (max) {
          // 최대값만 있으면
          tags.push(`${field.label}: ${max}${getUnit(field.key)} 이하`);
        }
      }
    });
    
    // 단위 반환 함수
    function getUnit(key) {
      const units = {
        '층수': '층',
        '총층수': '층',
        '평수': '평',
        '보증금': '만원',
        '월세': '만원',
        '관리비': '만원',
        '권리비': '만원'
      };
      return units[key] || '';
    }
    
    return tags;
  }, []);

  // 관심 매물 클릭 핸들러
  const handleInterestedPropertyClick = useCallback((propertyName) => {
    const property = allProperties.find(p => (p.상호 || p.name) === propertyName);
    if (property) {
      setSelectedProperty(property);
      setIsPropertySidebarOpen(true);
    }
  }, [allProperties]);

  const containerStyle = availableHeight
    ? { height: `${availableHeight}px`, minHeight: `${MIN_CONSULTATION_HEIGHT}px` }
    : { minHeight: `${MIN_CONSULTATION_HEIGHT}px` };

  return (
    <div
      className="w-full relative flex flex-col bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
      style={containerStyle}
    >
      {/* 로딩 표시 */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
            <span className="text-sm text-gray-700">상담 데이터를 불러오는 중...</span>
          </div>
        </div>
      )}

      {/* 에러 표시 */}
      {error && !isLoading && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-red-50 border border-red-200 px-4 py-2 rounded-lg shadow-lg z-10">
          <span className="text-red-600 text-sm">⚠️ {error}</span>
        </div>
      )}

      {/* 상담 목록 컨테이너 */}
      <div className="flex-1 overflow-auto p-4 md:p-6">
        {consultationData.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <div className="text-4xl mb-4">📋</div>
            <div className="text-lg font-medium mb-2">상담 일지가 없습니다</div>
            <div className="text-sm">우측 하단의 버튼을 눌러 새 상담 일지를 작성하세요.</div>
          </div>
        ) : (
          <div className="space-y-4">
            {consultationData.map((consultation) => (
              <ConsultationCard
                key={consultation.id}
                consultation={consultation}
                onEdit={() => handleConsultationClick(consultation)}
                onDelete={(e) => handleDeleteConsultation(e, consultation.id)}
                filtersToTags={filtersToTags}
                onInterestedPropertyClick={handleInterestedPropertyClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* 퀵 메뉴 버튼 */}
      <ButtonListFunction
        actions={[
          {
            key: 'add-consultation',
            label: '➕',
            title: '상담 추가',
            onClick: () => setIsAddConsultationOpen(true),
            color: 'bg-blue-500 hover:bg-blue-600'
          },
          {
            key: 'rollback',
            label: '↩️',
            title: '데이터 롤백',
            onClick: () => setIsRollbackModalOpen(true),
            color: 'bg-purple-500 hover:bg-purple-600'
          }
        ]}
        disabled={isLoading}
      />

      {/* 상담 추가 모달 */}
      <ConsumerCard
        isOpen={isAddConsultationOpen}
        onClose={() => setIsAddConsultationOpen(false)}
        onSuccess={handleConsultationAdded}
        existingProperties={[]}
      />

      {/* 상담 편집 모달 */}
      <ConsumerCard
        isOpen={!!editingConsultation}
        onClose={() => setEditingConsultation(null)}
        onSuccess={handleConsultationUpdated}
        existingProperties={[]}
        initialData={editingConsultation}
        isEditMode={true}
      />

      {/* 매물 사이드바 */}
      <PropertySidebar
        property={selectedProperty}
        isOpen={isPropertySidebarOpen}
        onClose={() => {
          setIsPropertySidebarOpen(false);
          setSelectedProperty(null);
        }}
        onFocusMap={onFocusMap}
        onAddInterestedProperty={handleAddInterestedPropertyLocal}
        onPropertyUpdate={loadProperties}
      />

      {/* 롤백 모달 */}
      <RollbackModal
        isOpen={isRollbackModalOpen}
        onClose={() => setIsRollbackModalOpen(false)}
        onSuccess={() => {
          // 롤백 성공 시 페이지 새로고침
          window.location.reload();
        }}
      />

      {/* 관심 매물 등록 모달 (로컬 - App.js에서 전달되지 않은 경우 대비) */}
      {!onAddInterestedProperty && (
        <InterestedPropertyModal
          isOpen={isInterestedPropertyModalOpen}
          onClose={() => {
            setIsInterestedPropertyModalOpen(false);
            setPropertyForInterested(null);
          }}
          property={propertyForInterested}
          onSuccess={() => {
            loadConsultations();
          }}
        />
      )}
    </div>
  );
}

export default MainConsultation;

